import { Component, Input, Output, EventEmitter, ViewChild, OnDestroy, forwardRef, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelectModule, MatSelect } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatOptionModule } from '@angular/material/core';
import { TranslatePipe } from '@ngx-translate/core';
import { LibraryCollectionFolder } from '@go-modules/services/library/library.service';

interface FolderDisplay extends LibraryCollectionFolder {
	level: number;
	isVisible: boolean;
	hasChildren: boolean;
	isExpanded: boolean;
}

@Component({
	selector: 'ngx-folder-tree-select',
	templateUrl: './folder-tree-select.component.html',
	styleUrls: ['./folder-tree-select.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		MatSelectModule,
		MatFormFieldModule,
		MatIconModule,
		MatButtonModule,
		MatDividerModule,
		MatOptionModule,
		TranslatePipe
	],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FolderTreeSelectComponent),
			multi: true
		}
	]
})
export class FolderTreeSelectComponent implements ControlValueAccessor, OnChanges, OnDestroy {
	@ViewChild('folderSelect') public folderSelect: MatSelect;
	@Input() public folders: LibraryCollectionFolder[] = [];
	@Input() public showNoneOption = true;
	@Input() public maxDepth: number | null = null;

	@Output() public availableFoldersChange = new EventEmitter<LibraryCollectionFolder[]>();
	public displayFolders: FolderDisplay[] = [];
	public selectedFolder: FolderDisplay | null = null;
	private pendingValue: LibraryCollectionFolder | null = null;

	private keydownHandler: ((event: KeyboardEvent) => void) | null = null;
	private onChange = (_value: any) => {};
	private onTouched = () => {};

	public writeValue (value: LibraryCollectionFolder | null): void {
		if (this.displayFolders.length === 0) {
			// Store the value to be set once folders are ready
			this.pendingValue = value;
			return;
		}

		if (value && this.displayFolders.length > 0) {
			const foundFolder = this.displayFolders.find((f) => f.id === value.id);
			if (foundFolder) {
				this.selectedFolder = foundFolder;
				this.expandPathToFolder(foundFolder);
			} else {
				this.selectedFolder = null;
				this.onChange(null);
				this.onTouched();
			}
		} else {
			this.selectedFolder = null;
			this.onChange(null);
			this.onTouched();
		}
	}

	public registerOnChange (fn: any): void {
		this.onChange = fn;
	}

	public registerOnTouched (fn: any): void {
		this.onTouched = fn;
	}

	public ngOnChanges (changes: any) {
		if (changes.folders || changes.maxDepth) {
			this.prepareFolderDisplay(this.folders);

			// Emit the available folders after filtering
			const availableFolders = this.displayFolders.map((df) => {
				const { ...folder } = df;
				return folder as LibraryCollectionFolder;
			});
			this.availableFoldersChange.emit(availableFolders);

			if (this.pendingValue !== null) {
				const temp = this.pendingValue;
				this.pendingValue = null;
				this.writeValue(temp);
			} else if (this.selectedFolder) {
				// Re-validate existing selection
				const currentSelection = this.selectedFolder;
				const foundFolder = this.displayFolders.find((f) => f.id === currentSelection.id);
				if (foundFolder) {
					this.selectedFolder = foundFolder;
					this.expandPathToFolder(foundFolder);
				} else {
					this.selectedFolder = null;
					this.onChange(null);
					this.onTouched();
				}
			}
		}
	}

	public ngOnDestroy () {
		if (this.keydownHandler && this.folderSelect) {
			const selectElement = this.folderSelect._elementRef.nativeElement;
			selectElement.removeEventListener('keydown', this.keydownHandler, true);
		}
	}

	public toggleFolderExpansion (folder: FolderDisplay, event: Event) {
		event.preventDefault();
		event.stopPropagation();

		folder.isExpanded = !folder.isExpanded;
		this.updateVisibility();
	}

	public onSelectOpenedChange (isOpen: boolean) {
		if (isOpen && this.folderSelect) {
			const selectElement = this.folderSelect._elementRef.nativeElement;

			// Store the handler so we can remove it later
			this.keydownHandler = (event: KeyboardEvent) => {
				this.handleKeyboardNavigation(event);
			};

			selectElement.addEventListener('keydown', this.keydownHandler, true);
		} else if (!isOpen && this.keydownHandler) {
			// Clean up the event listener when panel closes
			const selectElement = this.folderSelect._elementRef.nativeElement;
			selectElement.removeEventListener('keydown', this.keydownHandler, true);
			this.keydownHandler = null;
		}
	}

	public onFolderSelected (event: any) {
		const value = event.value;
		this.selectedFolder = value;
		this.onChange(value);
		this.onTouched();
	}

	public getFolderOptionClass (folder: FolderDisplay): string {
		return folder.isVisible ? 'folder-option-visible' : 'folder-option-hidden';
	}

	/**
	 * Expands all parent folders leading to the specified folder
	 * to ensure the folder is visible in the tree
	 */
	private expandPathToFolder (targetFolder: FolderDisplay): void {
		const pathToRoot: FolderDisplay[] = [];
		let currentFolder: FolderDisplay | undefined = targetFolder;

		while (currentFolder && currentFolder.parent_id !== null) {
			const parentFolder = this.displayFolders.find((f) => f.id === currentFolder!.parent_id);
			if (parentFolder) {
				pathToRoot.push(parentFolder);
				currentFolder = parentFolder;
			} else {
				break;
			}
		}

		// Expand all folders in the path
		pathToRoot.forEach((folder) => {
			if (folder.hasChildren && !folder.isExpanded) {
				folder.isExpanded = true;
			}
		});

		this.updateVisibility();
	}

	/**
	 * Calculates the depth level of a folder
	 * Root folders (parent_id === null) are level 1
	 */
	private calculateFolderDepth (
		folder: LibraryCollectionFolder, folderMap: Map<number, LibraryCollectionFolder>
	): number {
		let depth = 1;
		let currentId = folder.parent_id;

		while (currentId !== null) {
			depth++;
			const parent = folderMap.get(currentId);
			if (parent) {
				currentId = parent.parent_id;
			} else {
				break;
			}
		}

		return depth;
	}

	private prepareFolderDisplay (folders: LibraryCollectionFolder[]) {
		// Create a map for quick folder lookup
		const folderMap = new Map<number, LibraryCollectionFolder>();
		folders.forEach((folder) => {
			folderMap.set(folder.id, folder);
		});

		// Filter folders based on maxDepth if specified
		let filteredFolders = folders;
		if (this.maxDepth !== null && this.maxDepth > 0) {
			filteredFolders = folders.filter((folder) => {
				const depth = this.calculateFolderDepth(folder, folderMap);
				return depth <= this.maxDepth!;
			});
		}

		// Create a map to check which folders have children (within the filtered set)
		const filteredIds = new Set(filteredFolders.map((f) => f.id));
		const parentIds = new Set(
			filteredFolders
				.map((f) => f.parent_id)
				.filter((id) => id !== null && filteredIds.has(id))
		);

		// Create display folders with additional properties
		const displayMap = new Map<number, FolderDisplay>();
		filteredFolders.forEach((folder) => {
			const displayFolder: FolderDisplay = {
				...folder,
				level: 0, // This represents indentation level, not depth
				isVisible: true,
				hasChildren: parentIds.has(folder.id),
				isExpanded: false
			};
			displayMap.set(folder.id, displayFolder);
		});

		// Calculate indentation levels (for display purposes)
		filteredFolders.forEach((folder) => {
			let level = 0;
			let currentId = folder.parent_id;

			while (currentId !== null) {
				// Only count parents that are in the filtered set
				if (displayMap.has(currentId)) {
					level++;
					const parent = displayMap.get(currentId);
					currentId = parent.parent_id;
				} else {
					break;
				}
			}

			const displayFolder = displayMap.get(folder.id);
			if (displayFolder) {
				displayFolder.level = level;
			}
		});

		// Build a proper tree order (depth-first traversal)
		const orderedFolders: FolderDisplay[] = [];

		// Helper function to add folder and its children in order
		const addFolderAndChildren = (parentId: number | null, folders: FolderDisplay[]) => {
			const children = folders
				.filter((f) => f.parent_id === parentId)
				.sort((a, b) => a.name.localeCompare(b.name));

			children.forEach((child) => {
				orderedFolders.push(child);
				// Recursively add this folder's children
				addFolderAndChildren(child.id, folders);
			});
		};

		const allFolders = Array.from(displayMap.values());
		// Start with root folders (parent_id === null)
		addFolderAndChildren(null, allFolders);

		this.displayFolders = orderedFolders;

		// Initially show only root level folders
		this.updateVisibility();
	}

	private updateVisibility () {
		const expandedIds = new Set(
			this.displayFolders
				.filter((f) => f.isExpanded)
				.map((f) => f.id)
		);

		this.displayFolders.forEach((folder) => {
			if (folder.parent_id === null) {
				// Root folders are always visible
				folder.isVisible = true;
			} else {
				// Check if all ancestors are expanded
				let isVisible = true;
				let currentId = folder.parent_id;

				while (currentId !== null) {
					if (!expandedIds.has(currentId)) {
						isVisible = false;
						break;
					}
					const parent = this.displayFolders.find((f) => f.id === currentId);
					// We can assert parent exists because prepareFolderDisplay ensures consistency
					currentId = parent ? parent.parent_id : null;
				}
				folder.isVisible = isVisible;
			}
		});
	}

	private handleKeyboardNavigation (event: KeyboardEvent) {
		const focusedIndex = this.folderSelect._keyManager?.activeItemIndex;
		if (focusedIndex === undefined || focusedIndex === null) return;

		// Handle up/down arrow keys to skip hidden items
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault();
			event.stopPropagation();

			const direction = event.key === 'ArrowDown' ? 1 : -1;
			let newIndex = focusedIndex;
			let found = false;

			// Start searching from the next/previous index
			newIndex += direction;

			// Keep searching until we find a visible option or reach the bounds
			while (!found) {
				// Handle bounds
				if (newIndex < 0) {
					// Wrap to last visible option
					for (let i = this.displayFolders.length - 1 + (this.showNoneOption ? 1 : 0); i >= 0; i--) {
						if (i === 0 && this.showNoneOption) {
							// This is the "None" option, which is always visible
							newIndex = i;
							found = true;
							break;
						}
						const folderIndex = this.showNoneOption ? i - 1 : i;
						if (folderIndex >= 0
							&& folderIndex < this.displayFolders.length
							&& this.displayFolders[folderIndex].isVisible
						) {
							newIndex = i;
							found = true;
							break;
						}
					}
					if (!found) newIndex = focusedIndex; // Stay at current if no visible option found
					break;
				}

				const totalOptions = this.displayFolders.length + (this.showNoneOption ? 1 : 0);
				if (newIndex >= totalOptions) {
					// Wrap to first visible option
					for (let i = 0; i < totalOptions; i++) {
						if (i === 0 && this.showNoneOption) {
							// This is the "None" option, which is always visible
							newIndex = i;
							found = true;
							break;
						}
						const folderIndex = this.showNoneOption ? i - 1 : i;
						if (folderIndex >= 0
							&& folderIndex < this.displayFolders.length
							&& this.displayFolders[folderIndex].isVisible
						) {
							newIndex = i;
							found = true;
							break;
						}
					}
					if (!found) newIndex = focusedIndex; // Stay at current if no visible option found
					break;
				}

				// Check if this index corresponds to a visible folder
				if (newIndex === 0 && this.showNoneOption) {
					// This is the "None" option, which is always visible
					found = true;
				} else {
					const folderIndex = this.showNoneOption ? newIndex - 1 : newIndex;
					if (folderIndex >= 0 && folderIndex < this.displayFolders.length) {
						if (this.displayFolders[folderIndex].isVisible) {
							found = true;
						} else {
							newIndex += direction;
						}
					}
				}
			}

			// Set the new active item
			this.folderSelect._keyManager?.setActiveItem(newIndex);
			return;
		}

		// Handle left/right arrow keys for expand/collapse
		if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
			return;
		}

		// Determine which folder is currently focused
		let currentFolder: FolderDisplay | null = null;

		if (focusedIndex === 0 && this.showNoneOption) {
			// "None" option is selected, no expand/collapse actions
			return;
		}

		const folderIndex = this.showNoneOption ? focusedIndex - 1 : focusedIndex;
		if (folderIndex >= 0 && folderIndex < this.displayFolders.length) {
			currentFolder = this.displayFolders[folderIndex];
		}

		if (!currentFolder || !currentFolder.isVisible) return;

		if (event.key === 'ArrowRight') {
			// Expand folder if it has children and is collapsed
			if (currentFolder.hasChildren && !currentFolder.isExpanded) {
				event.preventDefault();
				event.stopPropagation();
				currentFolder.isExpanded = true;
				this.updateVisibility();

				// Maintain focus position after re-render
				requestAnimationFrame(() => {
					this.folderSelect._keyManager?.setActiveItem(focusedIndex);
				});
			}
		} else if (event.key === 'ArrowLeft') {
			if (currentFolder.hasChildren && currentFolder.isExpanded) {
				// Collapse folder if it's expanded
				event.preventDefault();
				event.stopPropagation();
				currentFolder.isExpanded = false;
				this.updateVisibility();

				// Maintain focus position after re-render
				requestAnimationFrame(() => {
					this.folderSelect._keyManager?.setActiveItem(focusedIndex);
				});
			} else if (currentFolder.parent_id !== null) {
				// Navigate to parent folder
				event.preventDefault();
				event.stopPropagation();

				// Find parent in all folders (not just visible)
				const parentIndexInAll = this.displayFolders.findIndex((f) => f.id === currentFolder!.parent_id);
				if (parentIndexInAll >= 0) {
					// Add 1 if root option is shown, and add parent's index
					const adjustedIndex = this.showNoneOption ? parentIndexInAll + 1 : parentIndexInAll;
					this.folderSelect._keyManager?.setActiveItem(adjustedIndex);
				}
			}
		}
	}
}
